import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { environment } from '@environments/environment';
import { ERR_MESSAGES } from '@shared/constants/messages.constants';
import { PERMISSIONS } from '@shared/constants/permission.constants';
import { ROUTE_PARAMS } from '@shared/constants/route-param.constants';
import { SITE_LINKS } from '@shared/constants/site-links.constants';
import { STORAGE } from '@shared/constants/storage.constants';
import { TOAST_TYPE } from '@shared/constants/toast.constants';
import { UserPermissions } from '@shared/models/user/user.model';
import { PermissionService } from '@shared/services/permission/permission.service';
import { StorageService } from '@shared/services/storage/storage.service';
import { ToastService } from '@shared/services/toast/toast.service';
import { buildUrl } from '@shared/utilities/common.utils';
import { checkServiceConfigRoutePermission } from '@shared/utilities/service-config-permissions.util';
import { map } from 'rxjs';

function findInRouteTree<T>(
  route: ActivatedRouteSnapshot,
  selector: (snapshot: ActivatedRouteSnapshot) => T | undefined
): T | undefined {
  for (let i = route.pathFromRoot.length - 1; i >= 0; i--) {
    const result = selector(route.pathFromRoot[i]);
    if (result !== undefined) {
      return result;
    }
  }
  return undefined;
}

function resolveRouteData(route: ActivatedRouteSnapshot, key: string): unknown {
  return findInRouteTree(route, snapshot => {
    if (snapshot.data?.[key] === undefined) {
      return undefined;
    }
    return snapshot.data[key];
  });
}

function resolveClientId(route: ActivatedRouteSnapshot): string | undefined {
  return findInRouteTree(
    route,
    snapshot => snapshot.params[ROUTE_PARAMS.CLIENT_ID] || undefined
  );
}

function persistUserPermissions(
  storageService: StorageService,
  userPermissions: UserPermissions
): void {
  const permissions: UserPermissions = {
    clientPermissions: userPermissions.clientPermissions.map(item => ({
      permissionId: item.permissionId,
    })),
    customerPermissions: userPermissions.customerPermissions.map(item => ({
      permissionId: item.permissionId,
    })),
  };
  storageService.set(STORAGE.USER_PERMISSIONS, JSON.stringify(permissions));
}

function denyAccess(
  router: Router,
  toastService: ToastService,
  deniedRedirect: string | undefined,
  clientId: string | undefined
): UrlTree {
  toastService.show(ERR_MESSAGES.FORBIDDEN, TOAST_TYPE.ERROR, 5, false, false, true);

  if (clientId) {
    return router.parseUrl(
      buildUrl(deniedRedirect ?? SITE_LINKS.CLIENT.DETAILS, clientId)
    );
  }

  return router.parseUrl(SITE_LINKS.AUTH.LOGIN);
}

function evaluateRoutePermissions(
  permissionService: PermissionService,
  permission: PERMISSIONS | undefined,
  permissions: PERMISSIONS[] | undefined,
  permissionMode: 'any' | 'all' | undefined
): boolean {
  if (permissions?.length) {
    const mode = permissionMode ?? 'any';
    return mode === 'all'
      ? permissions.every(item => checkServiceConfigRoutePermission(permissionService, item))
      : permissions.some(item => checkServiceConfigRoutePermission(permissionService, item));
  }

  if (!permission) {
    return true;
  }

  return checkServiceConfigRoutePermission(permissionService, permission);
}

export const permissionGuard: CanActivateFn = route => {
  const permission = resolveRouteData(route, 'permission') as PERMISSIONS | undefined;
  const permissions = resolveRouteData(route, 'permissions') as PERMISSIONS[] | undefined;
  const permissionMode = resolveRouteData(route, 'permissionMode') as 'any' | 'all' | undefined;

  if (!permission && !permissions?.length) {
    return true;
  }

  const permissionService = inject(PermissionService);
  const router = inject(Router);
  const toastService = inject(ToastService);
  const storageService = inject(StorageService);
  const deniedRedirect = resolveRouteData(route, 'permissionDeniedRedirect') as string | undefined;
  const clientId = resolveClientId(route);

  if (storageService.get(STORAGE.USER_PERMISSIONS)) {
    if (evaluateRoutePermissions(permissionService, permission, permissions, permissionMode)) {
      return true;
    }
    return denyAccess(router, toastService, deniedRedirect, clientId);
  }

  return permissionService
    .getLoggedInUserPermissions({ clientId: clientId ?? environment.CLIENT_ID })
    .pipe(
      map(userPermissions => {
        persistUserPermissions(storageService, userPermissions);
        if (evaluateRoutePermissions(permissionService, permission, permissions, permissionMode)) {
          return true;
        }
        return denyAccess(router, toastService, deniedRedirect, clientId);
      })
    );
};
